import { useState, useEffect, useRef } from 'react';
import { SavChatRepository } from '@/services/firebase/sav-chat-repository';
import { SavChat } from '@/models/savChat';
import { useCustomer } from '@/app/providers';

/**
 * Hook pour gérer les notifications de nouveaux messages SAV côté client
 * Récupère les chats non lus par le client et détecte les nouveaux messages
 */
export const useCustomerSavNotifications = () => {
    const { customerId } = useCustomer();
    const [unreadChats, setUnreadChats] = useState<SavChat[]>([]);
    const [lastNotification, setLastNotification] = useState<{ chat: SavChat, timestamp: number } | null>(null);

    // Pour détecter les nouveaux messages par différence
    const previousChatsUnreadRef = useRef<Map<string, number>>(new Map());
    const isFirstLoadRef = useRef(true);

    // Délai initial pour éviter les notifications au chargement
    useEffect(() => {
        const timer = setTimeout(() => {
            isFirstLoadRef.current = false;
        }, 2000);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (!customerId) {
            // console.log('🔕 useCustomerSavNotifications: No customerId provided');
            setUnreadChats([]);
            return;
        }

        // console.log('🔔 useCustomerSavNotifications: Subscribing to chats for customerId:', customerId);

        // Écouter les chats du customer via le repository
        const unsubscribe = SavChatRepository.subscribeToCustomerChats(customerId, (chats) => {
            // console.log(`🔔 useCustomerSavNotifications: Received ${chats.length} chats update`);
            let hasNewMessage = false;
            let lastNewMessageChat: SavChat | null = null;

            // Détection des nouveaux messages
            chats.forEach(chat => {
                const unread = chat.unreadByCustomer || 0;
                const prevUnread = previousChatsUnreadRef.current.get(chat.id) || 0;

                // Si le nombre de messages non lus augmente, c'est un nouveau message
                if (!isFirstLoadRef.current && unread > prevUnread) {
                    // console.log('🔔 useCustomerSavNotifications: New message detected in chat', chat.id);
                    hasNewMessage = true;
                    lastNewMessageChat = chat;
                }
                // Mettre à jour la ref pour la prochaine comparaison
                previousChatsUnreadRef.current.set(chat.id, unread);
            });

            // Filtrer les chats qui ont des messages non lus par le CUSTOMER
            const unreadList = chats
                .filter(c => c.unreadByCustomer > 0)
                // Trier par date du dernier message (récent -> ancien)
                .sort((a, b) => {
                    const dateA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
                    const dateB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
                    return dateB - dateA;
                });

            // console.log(`🔔 useCustomerSavNotifications: ${unreadList.length} unread chats for customer`);
            setUnreadChats(unreadList);

            // Déclencher la notification visuelle si nouveau message
            if (hasNewMessage && lastNewMessageChat) {
                setLastNotification({
                    chat: lastNewMessageChat,
                    timestamp: Date.now()
                });
            }
        });

        return () => unsubscribe();
    }, [customerId]);

    return { unreadChats, lastNotification };
};
